import type { ReactNode } from "react";
import { ControlOutlined } from "@ant-design/icons";
import clsx from "clsx";
import { TitlePageFillersDropdown } from "@/entities/journal";
import { ConnectIcon } from "@/shared/assets/svg/icons";
import { useJournalIntegrationTag, type JournalIntegrationTagKind } from "../../model";
import "./journal-integration-tags.scss";

type JournalIntegrationTagsProps = {
  variant?: "inline" | "banner";
};

const AdminLink = ({ text }: { text: string }) => (
  <TitlePageFillersDropdown text={text} />
);

const getTagContent = (
  kind: Exclude<JournalIntegrationTagKind, "integration-ok">,
): { className: string; content: ReactNode } => {
  switch (kind) {
    case "interaction-error":
      return {
        className: "journal-integration-tag--error",
        content: (
          <>
            Возникла техническая ошибка при отправке в орган Надзора.{" "}
            <AdminLink text="Обратитесь к администратору" />
          </>
        ),
      };
    case "document-error":
      return {
        className: "journal-integration-tag--error",
        content: (
          <>
            Возникла ошибка подготовки XML-документа. Повторите отправку или{" "}
            <AdminLink text="обратитесь к администратору" />
          </>
        ),
      };
    case "settings-mismatch":
      return {
        className: "journal-integration-tag--error",
        content: (
          <>
            Ошибка интеграции, невозможно отправить в орган Надзора.{" "}
            <AdminLink text="Обратитесь к администратору" />
          </>
        ),
      };
    case "no-integration":
      return {
        className: "journal-integration-tag--info",
        content: (
          <>
            Настройка интеграции с органом надзора отсутствует.{" "}
            <AdminLink text="Обратитесь к администратору" />
          </>
        ),
      };
    default:
      return {
        className: "journal-integration-tag--info",
        content: null,
      };
  }
};

const IntegrationOkIcon = () => (
  <span
    className={clsx(
      "journal-integration-tag",
      "journal-integration-tag--success-icon",
    )}
    aria-label="Интеграция с органом надзора настроена"
  >
    <ControlOutlined />
  </span>
);

export const JournalIntegrationTags = ({
  variant = "banner",
}: JournalIntegrationTagsProps) => {
  const { kind } = useJournalIntegrationTag();

  if (!kind) {
    return null;
  }

  if (variant === "inline") {
    if (kind !== "integration-ok") {
      return null;
    }

    return <IntegrationOkIcon />;
  }

  if (kind === "integration-ok") {
    return null;
  }

  const tag = getTagContent(kind);

  return (
    <div className="journal-integration-tags">
      <div className={clsx("journal-integration-tag", tag.className)}>
        {kind === "no-integration" ? (
          <span className="journal-integration-tag__icon" aria-hidden>
            <ConnectIcon />
          </span>
        ) : null}
        <span className="journal-integration-tag__text">{tag.content}</span>
      </div>
    </div>
  );
};
